import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { deviceService } from '@/services/api'
import api from '@/services/api'
import {
  Monitor, Wifi, WifiOff, RefreshCw, Plus, Edit,
  Trash2, Users, Clock, AlertCircle, CheckCircle2,
  Loader, Radio, ShieldCheck, MapPin, Zap, Activity,
  ChevronRight, Signal, Cpu, DoorOpen, CalendarClock, ShieldOff, Shield
} from 'lucide-react'
import { Skeleton } from '@/components/common/Skeleton'
import DeviceModal from '@/components/devices/DeviceModal'
import Modal from '@/components/common/Modal'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import { ru } from 'date-fns/locale'
import clsx from 'clsx'

// ── Ultra-Clean Stat Card ──────────────────────────────

function SummaryStat({ label, value, colorClass, icon: Icon }) {
  const themes = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-100",
    rose: "text-rose-600 bg-rose-50 border-rose-100",
    indigo: "text-indigo-600 bg-indigo-50 border-indigo-100",
  }
  return (
    <div className="bg-white border border-slate-200 rounded-2xl md:rounded-[24px] p-3 md:p-6 flex flex-col md:flex-row items-center md:items-center gap-2 md:gap-5 shadow-sm">
      <div className={clsx("p-2 md:p-3.5 rounded-xl md:rounded-2xl border", themes[colorClass])}>
        <Icon size={16} strokeWidth={2.5} className="md:hidden" />
        <Icon size={22} strokeWidth={2.5} className="hidden md:block" />
      </div>
      <div className="text-center md:text-left">
        <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.15em] md:tracking-[0.2em] text-slate-400 mb-0.5 md:mb-1">{label}</p>
        <p className="text-xl md:text-3xl font-black text-slate-900 leading-none tracking-tighter">{value}</p>
      </div>
    </div>
  )
}

const TYPE_CONFIG = {
  entry: { label: 'ENTRY', color: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: '🚪' },
  exit: { label: 'EXIT', color: 'bg-amber-50 text-amber-700 border-amber-100', icon: '🔚' },
  both: { label: 'FULL_ACCESS', color: 'bg-indigo-50 text-indigo-700 border-indigo-100', icon: '↕️' },
}

export default function DevicesPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState({ open: false, data: null })
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' })
  const [pinging, setPinging] = useState({})
  const [syncing, setSyncing] = useState({})
  const [opening, setOpening] = useState({})
  const [applyingSchedule, setApplyingSchedule] = useState({})
  const [blocking, setBlocking] = useState({})
  const [deleting, setDeleting] = useState(false)
  const [typeFilter, setTypeFilter] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () => deviceService.list().then((r) => r.data.results || r.data),
    refetchInterval: 60_000,
  })

  // ---Handlers (Логика сохранена)---
  const handlePing = async (device) => {
    setPinging((p) => ({ ...p, [device.id]: true }))
    try {
      const resp = await deviceService.ping(device.id)
      if (resp.data.online) toast.success(`Терминал ${device.name}: ONLINE`)
      else toast.error(`Терминал ${device.name}: NO_SIGNAL`)
      qc.invalidateQueries(['devices'])
    } catch { toast.error('Протокол: Ошибка опроса') }
    finally { setPinging((p) => ({ ...p, [device.id]: false })) }
  }

  const handleSync = async (device, syncType) => {
    const label = syncType === 'employees' ? 'сотрудников' : 'студентов'
    if (!window.confirm(`Загрузить ${label} на терминал "${device.name}"?`)) return
    setSyncing((s) => ({ ...s, [device.id]: syncType }))
    try {
      const resp = await api.post(`/devices/${device.id}/sync-users/`, { force: true, sync_type: syncType })
      toast.success(resp.data.message)
    } catch { toast.error('Синхронизация отклонена') }
    finally { setSyncing((s) => ({ ...s, [device.id]: false })) }
  }

  const handleOpenDoor = async (device) => {
    if (!window.confirm(`Открыть турникет "${device.name}"?`)) return
    setOpening((o) => ({ ...o, [device.id]: true }))
    try {
      const resp = await deviceService.openDoor(device.id)
      toast.success(resp.data.message || 'Турникет открыт')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка открытия турникета')
    } finally {
      setOpening((o) => ({ ...o, [device.id]: false }))
    }
  }

  const handleApplySchedule = async (device) => {
    if (!window.confirm(
      `Настроить рабочие часы на терминале "${device.name}"?\n\nВремя: 07:00–19:00 (Пн-Сб)\nВсе пользователи будут обновлены в фоне.`
    )) return
    setApplyingSchedule((s) => ({ ...s, [device.id]: true }))
    try {
      const resp = await deviceService.applyWorkingHours(device.id, {
        start_time: '07:00:00',
        end_time: '19:00:00',
        work_days: [1, 2, 3, 4, 5, 6],
      })
      toast.success(resp.data.message || 'Расписание применено')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка настройки расписания')
    } finally {
      setApplyingSchedule((s) => ({ ...s, [device.id]: false }))
    }
  }

  const handleToggleBlock = async (device) => {
    const action = device.is_blocked ? 'разблокировать проход' : 'ЗАБЛОКИРОВАТЬ ПРОХОД'
    if (!window.confirm(`${action} для терминала "${device.name}"?`)) return
    setBlocking((b) => ({ ...b, [device.id]: true }))
    try {
      const resp = await deviceService.toggleBlock(device.id)
      toast.success(resp.data.message)
      qc.invalidateQueries(['devices'])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка переключения блокировки')
    } finally {
      setBlocking((b) => ({ ...b, [device.id]: false }))
    }
  }

  const handlePingAll = async () => {
    const tid = toast.loading('Инициализация глобального опроса...')
    try {
      await api.post('/devices/ping-all/')
      toast.success('Запрос отправлен в очередь', { id: tid })
      setTimeout(() => qc.invalidateQueries(['devices']), 3000)
    } catch { toast.error('Сбой сети', { id: tid }) }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/devices/${deleteModal.id}/`)
      toast.success('Устройство исключено из системы')
      qc.invalidateQueries(['devices'])
      setDeleteModal({ open: false, id: null, name: '' })
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления')
    } finally { setDeleting(false) }
  }

  const allDevices = data || []
  const filteredDevices = typeFilter ? allDevices.filter((d) => d.device_type === typeFilter) : allDevices
  const onlineCount = allDevices.filter((d) => d.status === 'online').length
  const offlineCount = allDevices.filter((d) => d.status !== 'online').length

  return (
    <div className="min-h-screen bg-[#F8F9FB] p-4 md:p-6 lg:p-12 text-slate-900 font-sans selection:bg-indigo-100 pb-24 md:pb-6">

      {/* Header Section (Apple Style) */}
      <header className="max-w-[1600px] mx-auto mb-6 md:mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-8">
        <div>
          <div className="flex items-center justify-between md:block">
            <div className="flex items-center gap-3 mb-0 md:mb-4">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-100">
                <ShieldCheck className="text-white w-6 h-6" />
              </div>
              <div>
                <h1 className="text-base md:text-lg font-black tracking-tight text-slate-900 leading-none">SENTINEL.HARDWARE</h1>
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 mt-1">Terminal Infrastructure</p>
              </div>
            </div>
            {/* Mobile header actions */}
            <div className="flex items-center gap-2 md:hidden">
              <button
                onClick={handlePingAll}
                className="w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all shadow-sm"
                title="Ping All"
              >
                <Radio size={16} className="text-indigo-500" />
              </button>
            </div>
          </div>
          <h2 className="text-2xl md:text-5xl font-black text-slate-900 tracking-tighter leading-none mt-3 mb-1 md:mb-3">Устройства.</h2>
          <p className="text-slate-400 font-medium text-sm hidden md:block max-w-md">Управление парком терминалов Hikvision и мониторинг состояния сети.</p>
        </div>

        {/* Desktop header actions */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={handlePingAll}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl border border-slate-200 bg-white text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
          >
            <Radio size={16} className="text-indigo-500" /> Ping All Nodes
          </button>
          <button
            onClick={() => setModal({ open: true, data: null })}
            className="flex items-center gap-2 px-7 py-3 rounded-2xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all"
          >
            <Plus size={18} /> Register Node
          </button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto space-y-6 md:space-y-12">

        {/* Stats Summary Grid */}
        <section className="grid grid-cols-3 gap-3 md:gap-6">
          <SummaryStat label="Онлайн" value={onlineCount} icon={CheckCircle2} colorClass="emerald" />
          <SummaryStat label="Офлайн" value={offlineCount} icon={WifiOff} colorClass="rose" />
          <SummaryStat label="Всего" value={allDevices.length} icon={Cpu} colorClass="indigo" />
        </section>

        {/* Filter Tabs (Segmented Control) */}
        {!isLoading && allDevices.length > 0 && (
          <div className="flex p-1.5 bg-white border border-slate-200 rounded-2xl shadow-sm w-full sm:w-fit overflow-x-auto">
            {[
              { v: '', l: 'Все терминалы' },
              { v: 'entry', l: 'ENTRY_ONLY' },
              { v: 'exit', l: 'EXIT_ONLY' },
              { v: 'both', l: 'BIDIRECTIONAL' },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setTypeFilter(f.v)}
                className={clsx(
                  'px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center gap-3',
                  typeFilter === f.v
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                    : 'text-slate-400 hover:text-slate-900'
                )}
              >
                {f.l}
                <span className={clsx(
                  'px-1.5 py-0.5 rounded-md text-[9px] font-black border',
                  typeFilter === f.v ? 'bg-indigo-500 border-indigo-400 text-white' : 'bg-slate-100 border-slate-200 text-slate-500'
                )}>
                  {f.v ? allDevices.filter(d => d.device_type === f.v).length : allDevices.length}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Device Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-[32px] h-72 animate-pulse border border-slate-200 shadow-sm" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredDevices.map((device) => {
              const typeConf = TYPE_CONFIG[device.device_type] || TYPE_CONFIG.both
              const isOnline = device.status === 'online'
              const isBlocked = device.is_blocked

              return (
                <div
                  key={device.id}
                  className={clsx(
                    "group relative bg-white border rounded-[32px] p-7 shadow-sm transition-all duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.04)]",
                    isBlocked ? "border-rose-300 bg-rose-50/30" : "border-slate-200 hover:border-indigo-200"
                  )}
                >
                  {/* Status Side Marker */}
                  <div className={clsx(
                    "absolute top-10 left-0 w-1 h-14 rounded-r-full transition-colors",
                    isBlocked ? "bg-rose-600 shadow-[0_0_10px_#e11d48]" : isOnline ? "bg-emerald-500 shadow-[0_0_10px_#10b981]" : "bg-rose-500 shadow-[0_0_10px_#f43f5e]"
                  )} />

                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-8">
                    <div className="flex items-center gap-4">
                      <div className={clsx(
                        "w-14 h-14 rounded-2xl flex items-center justify-center text-xl border transition-transform group-hover:scale-110",
                        isOnline ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"
                      )}>
                        {typeConf.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-slate-900 leading-none tracking-tight">{device.name}</h3>
                        <p className="text-[11px] font-mono font-bold text-slate-400 mt-2 tracking-[0.1em]">{device.ip_address}</p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <span className={clsx(
                        "px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm",
                        isOnline ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-rose-50 text-rose-600 border-rose-100"
                      )}>
                        {isOnline ? 'Active' : 'Missing'}
                      </span>
                      {isBlocked && (
                        <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border bg-rose-600 text-white border-rose-600 shadow-sm">
                          Blocked
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Info Grid (Protocol Style) */}
                  <div className="space-y-4 mb-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-400">
                        <MapPin size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Location</span>
                      </div>
                      <span className="text-xs font-black text-slate-700">{device.location}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Activity size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Protocol</span>
                      </div>
                      <span className={clsx("text-[9px] px-2.5 py-1 rounded-lg font-black uppercase border", typeConf.color)}>
                        {typeConf.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Signal size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Heartbeat</span>
                      </div>
                      <span className="text-xs font-bold text-slate-500">
                        {device.last_seen
                          ? formatDistanceToNow(new Date(device.last_seen), { locale: ru, addSuffix: true })
                          : 'PROTOCOL_OFFLINE'}
                      </span>
                    </div>
                  </div>

                  {/* Action Group */}
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handlePing(device)}
                        disabled={pinging[device.id]}
                        className="flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-50 border border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-white hover:border-indigo-200 hover:text-indigo-600 transition-all shadow-sm"
                      >
                        {pinging[device.id] ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                        Ping Node
                      </button>
                      <button
                        onClick={() => handleOpenDoor(device)}
                        disabled={opening[device.id] || !isOnline}
                        className="flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-50 border border-emerald-100 text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:bg-emerald-600 hover:text-white disabled:opacity-50 transition-all shadow-sm"
                      >
                        {opening[device.id] ? <Loader size={12} className="animate-spin" /> : <DoorOpen size={14} />}
                        Открыть
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleSync(device, 'employees')}
                        disabled={!!syncing[device.id] || !isOnline}
                        className="flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-50 border border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-white hover:border-emerald-200 hover:text-emerald-600 disabled:opacity-50 transition-all shadow-sm"
                      >
                        {syncing[device.id] === 'employees' ? <Loader size={12} className="animate-spin" /> : <Users size={12} />}
                        Сотрудники
                      </button>
                      <button
                        onClick={() => handleSync(device, 'students')}
                        disabled={!!syncing[device.id] || !isOnline}
                        className="flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-50 border border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-white hover:border-indigo-200 hover:text-indigo-600 disabled:opacity-50 transition-all shadow-sm"
                      >
                        {syncing[device.id] === 'students' ? <Loader size={12} className="animate-spin" /> : <Users size={12} />}
                        Студенты
                      </button>
                    </div>

                    <button
                      onClick={() => handleApplySchedule(device)}
                      disabled={applyingSchedule[device.id] || !isOnline}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-50 border border-amber-100 text-[10px] font-black uppercase tracking-widest text-amber-700 hover:bg-amber-600 hover:text-white disabled:opacity-50 transition-all shadow-sm"
                    >
                      {applyingSchedule[device.id] ? <Loader size={12} className="animate-spin" /> : <CalendarClock size={14} />}
                      Расписание
                    </button>

                    <button
                      onClick={() => handleToggleBlock(device)}
                      disabled={blocking[device.id]}
                      className={clsx(
                        'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm border',
                        device.is_blocked
                          ? 'bg-rose-600 border-rose-600 text-white hover:bg-rose-700'
                          : 'bg-rose-50 border-rose-100 text-rose-700 hover:bg-rose-600 hover:text-white'
                      )}
                    >
                      {blocking[device.id]
                        ? <Loader size={12} className="animate-spin" />
                        : device.is_blocked ? <ShieldOff size={14} /> : <Shield size={14} />
                      }
                      {device.is_blocked ? 'Снять блокировку' : 'Блокировать проход'}
                    </button>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setModal({ open: true, data: device })}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all border border-indigo-100"
                      >
                        <Edit size={14} /> Edit Config
                      </button>
                      <button
                        onClick={() => setDeleteModal({ open: true, id: device.id, name: device.name })}
                        className="w-12 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all border border-rose-100"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Mobile FAB — Register Node */}
      <button
        onClick={() => setModal({ open: true, data: null })}
        className="fixed bottom-20 right-4 z-50 md:hidden w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-900 text-white shadow-2xl shadow-slate-400/30 hover:bg-slate-800 active:scale-95 transition-all"
        aria-label="Добавить терминал"
      >
        <Plus size={24} strokeWidth={2.5} />
      </button>

      {/* Modals & Logic */}
      <DeviceModal open={modal.open} onClose={() => setModal({ open: false, data: null })} device={modal.data} />

      <Modal
        open={deleteModal.open}
        onClose={() => !deleting && setDeleteModal({ open: false, id: null, name: '' })}
        title="Protocol: Deletion"
        size="sm"
        footer={
          <div className="flex gap-4 w-full px-4 pb-4">
            <button
              className="flex-1 py-4 rounded-2xl border border-slate-200 font-black text-[11px] uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all"
              onClick={() => setDeleteModal({ open: false, id: null, name: '' })}
              disabled={deleting}
            >
              Отмена
            </button>
            <button
              className="flex-1 py-4 rounded-2xl bg-rose-600 text-white font-black text-[11px] uppercase tracking-widest hover:bg-rose-700 shadow-xl shadow-rose-200 transition-all"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Процесс...' : 'Подтвердить'}
            </button>
          </div>
        }
      >
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-rose-100">
            <AlertCircle size={40} strokeWidth={2.5} />
          </div>
          <p className="text-slate-500 font-medium px-4 leading-relaxed">Вы инициировали процесс исключения терминала <br /><span className="text-slate-900 font-black">«{deleteModal.name}»</span> из сети SENTINEL.</p>
        </div>
      </Modal>
    </div>
  )
}